/**
 * TanStack Query の `query.state.status` と同型（`QueryStatus`）。
 * `@tanstack/query-core` は直接依存に含めず、ここで定義する。
 */
export type PollingQueryStatus = 'pending' | 'error' | 'success';

/**
 * 定点監視のデフォルト方針（プロダクト全体の合意の置き場所）。
 *
 * - **intervalMs**: 負荷と体感のバランス（必要なら画面単位で上書き）
 * - **pauseWhenDocumentHidden**: バックグラウンドタブでは止め、復帰後に interval から再開
 * - **pauseWhenOffline**: オフライン時の無駄打ちとエラー連打を避ける（回線復帰後は `refetchOnReconnect` 等と相性よい）
 * - **pauseWhileInErrorState**: 失敗中は間隔取得を止め、フォーカス戻り等の明示的な再試行に任せる
 */
export const POLLING_DEFAULTS = {
  intervalMs: 5000,
  pauseWhenDocumentHidden: true,
  pauseWhenOffline: true,
  pauseWhileInErrorState: true
} as const;
export type PollingEnvironmentOverrides = Partial<{
  pauseWhenDocumentHidden: boolean;
  pauseWhenOffline: boolean;
  pauseWhileInErrorState: boolean;
}>;
function isDocumentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}
function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * ビジネス条件を満たしたうえで、環境・クエリ状態に応じて interval を返すか `false` にする。
 * `refetchInterval` のコールバックからそのまま返す想定。
 *
 * `query` は `state.status` のみ参照するため、TanStack の `Query` 全体を型で拘束せずスライスで受ける。
 */
export function resolvePollingInterval(intervalMs: number, query: {
  state: {
    status: PollingQueryStatus;
  };
}, overrides?: PollingEnvironmentOverrides): number | false {
  const o = {
    ...POLLING_DEFAULTS,
    ...overrides
  };
  if (o.pauseWhileInErrorState && query.state.status === 'error') {
    return false;
  }
  if (o.pauseWhenDocumentHidden && !isDocumentVisible()) {
    return false;
  }
  if (o.pauseWhenOffline && isBrowserOffline()) {
    return false;
  }
  return intervalMs;
}